import Layout from '../../components/Layout';
import ProductCard from '../../components/ProductCard';
import DisclaimerNotice from '../../components/DisclaimerNotice';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const products = [
  {
    name: "ANUA Niacinamide 10 + TXA 4 Serum",
    description: "This serum combines niacinamide and tranexamic acid to help improve the appearance of uneven skin tone and dark marks over time, while remaining lightweight and suitable for daily use.",
    bestFor: "Uneven Tone & Dark Marks",
    benefits: ["Niacinamide + Tranexamic acid", "Lightweight formula", "Suitable for daily use"],
    imageUrl: "https://anua.com/cdn/shop/files/anua-us-ampoule-serum-30ml-niacinamide-10-txa-4-serum-1161173091.png?v=1763979435&width=800",
    affiliateLink: "https://amzn.to/4023uq0"
  },
  {
    name: "Vitamin C Serum (15%)",
    description: "A stable L-ascorbic acid formula that brightens and fades discoloration. Works well for all skin tones.",
    bestFor: "Dark Spots & Dullness",
    benefits: ["Brightens complexion", "Fades hyperpigmentation", "Antioxidant protection"]
  },
  {
    name: "Alpha Arbutin Serum",
    description: "A gentle melanin inhibitor that works over time to even skin tone without irritation.",
    bestFor: "All Skin Tones",
    benefits: ["Gentle formula", "Safe for melanin-rich skin", "Fades dark spots gradually"]
  },
  {
    name: "Niacinamide Serum (10%)",
    description: "Helps reduce melanin transfer and evens out skin tone. Excellent for post-inflammatory hyperpigmentation.",
    bestFor: "PIH & Uneven Tone",
    benefits: ["Addresses post-acne marks", "Brightening effect", "Strengthens barrier"]
  },
  {
    name: "Azelaic Acid (15-20%)",
    description: "Targets both hyperpigmentation and uneven texture. Particularly effective for darker skin tones.",
    bestFor: "Melanin-Rich Skin",
    benefits: ["Fades dark marks", "Reduces inflammation", "Safe for sensitive skin"]
  },
  {
    name: "Tranexamic Acid Serum",
    description: "Targets stubborn pigmentation and post-inflammatory marks. Gentle enough for daily use.",
    bestFor: "Stubborn Dark Spots",
    benefits: ["Addresses stubborn spots", "Works on melasma", "Suitable for all skin tones"]
  },
  {
    name: "Broad Spectrum SPF 50",
    description: "Essential for preventing dark spots from getting worse. Look for formulas without white cast.",
    bestFor: "All Skin Tones",
    benefits: ["Prevents new spots", "No white cast options", "Daily essential"]
  }
];

const DarkSpotsHyperpigmentation = () => {
  return (
    <Layout>
      {/* Hero */}
      <section data-testid="darkspots-hero" className="py-20 md:py-32 bg-[#F2EBE3]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link 
            to="/skincare" 
            className="inline-flex items-center gap-2 text-[#3A4D39] text-sm font-medium mb-6 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Skincare
          </Link>
          <p className="text-xs tracking-widest uppercase font-medium text-[#C88D74] mb-4">
            Skin Concern
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#1C1C1C] leading-tight mb-6">
            Dark Spots & Hyperpigmentation
          </h1>
          <p className="text-base md:text-lg text-[#374151] leading-relaxed">
            Whether from sun damage, acne scars, or hormonal changes, dark spots can be frustrating. These products help brighten and even out your skin tone over time.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-4">
            Understanding Hyperpigmentation
          </h2>
          <p className="text-[#374151] leading-relaxed mb-4">
            Hyperpigmentation occurs when excess melanin forms deposits in the skin. Common causes include sun exposure, hormonal changes, inflammation from acne, and skin injuries.
          </p>
          <p className="text-[#374151] leading-relaxed mb-4">
            <strong>Post-inflammatory hyperpigmentation (PIH)</strong> is especially common in melanin-rich skin tones. Even minor irritation—from acne, a scratch, or an overly harsh product—can trigger dark marks that linger for months.
          </p>
          <p className="text-[#374151] leading-relaxed">
            The good news? With consistent care, gentle products, and diligent sun protection, most hyperpigmentation can be significantly improved over time. The key is patience and avoiding further irritation.
          </p>
        </div>
      </section>

      {/* Products */}
      <section data-testid="darkspots-products" className="py-16 md:py-24 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-8">
            Recommended Products
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {products.map((product) => (
              <ProductCard key={product.name} {...product} />
            ))}
          </div>
        </div>
      </section>

      {/* How to Choose */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-6">
            How to Choose
          </h2>
          <ul className="space-y-4 text-[#374151]">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Sunscreen is non-negotiable:</strong> Without SPF, all other brightening efforts are wasted. Look for formulas that don't leave a white cast on deeper skin tones.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Avoid irritation:</strong> For melanin-rich skin, irritation can cause more dark spots. Gentle ingredients like alpha arbutin, niacinamide, and azelaic acid are safer bets than harsh acids.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Start with one active:</strong> Introduce brightening ingredients one at a time to monitor how your skin responds.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Be patient:</strong> Hyperpigmentation can take 3-6 months to visibly improve—sometimes longer for deeper marks.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>PIH tip:</strong> For post-inflammatory hyperpigmentation after breakouts, treat gently. Over-treating can cause more inflammation and more marks.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Melasma:</strong> Hormonal hyperpigmentation is stubborn—consult a dermatologist for best results.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-[#F5F5F0]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <DisclaimerNotice />
        </div>
      </section>
    </Layout>
  );
};

export default DarkSpotsHyperpigmentation;
